// 2026-09-23 - FormuLearning - Step 1 대조약·허가 품목 목록 2차 필터 (이슈 #73 P3, M901-08)
// 파일 위치: frontend/src/features/step1/rldFilter.ts
//
// 1차 검색 결과(대조약 + 허가 품목)를 화면에서 좁힌다. **켜면 포함 · 끄면 제외**(M901-08). 저장하지 않는다.
// 결과는 원래 목록의 **위치 번호** 배열이다 — 선택·펼침·비교가 원래 번호를 그대로 쓰게 한다.
// 총중량은 상세 조회분이라 조회 전 품목은 판정할 수 없다(검토 §3.2 U2) — `unknown` 구간으로 두고 화면이 조회한다.
// 구간 경계는 이하(`Q-260923-06`): ≤300 · 300<w≤600 · 600<w≤1000 · >1000.
// 2026-09-23 수정 - 취소·만료 품목 칩(기본 포함 — `Q-260923-03` 잠정 결정) · `permitStatusLabel`.
// 2026-11-03 수정 - 이슈 #105(`M1002-20`): 취소·만료 품목 **기본 제외**(잠정 «기본 포함» 번복). 칩을 켜면 다시 보인다 — 목록에서 없애지 않는다.

type Row = Record<string, unknown>

export type WeightBand = 'le300' | 'le600' | 'le1000' | 'gt1000' | 'none' | 'unknown'

export const WEIGHT_BANDS: { key: Exclude<WeightBand, 'unknown'>; label: string }[] = [
  { key: 'le300', label: '≤300' },
  { key: 'le600', label: '≤600' },
  { key: 'le1000', label: '≤1000' },
  { key: 'gt1000', label: '1000 초과' },
  { key: 'none', label: '산출 불가' },
]

export interface RldFilter {
  single: boolean // 단일성분 포함
  combo: boolean // 복합제 포함
  maker: string // 제조사 부분 일치
  excludedIngredients: string[] // 끈 성분 — 이 성분을 가진 품목은 제외
  weightOn: boolean // 총중량 필터 사용
  weightBands: Exclude<WeightBand, 'unknown'>[] // 켠 구간
  name: string // 약품명 부분 일치
  referenceOnly: boolean // 대조약만
  cancelled: boolean // 취소·만료 품목 포함 — 기본 제외(`M1002-20`)
}

export const defaultFilter = (): RldFilter => ({
  single: true,
  combo: true,
  maker: '',
  excludedIngredients: [],
  weightOn: false,
  weightBands: WEIGHT_BANDS.map((b) => b.key),
  name: '',
  referenceOnly: false,
  cancelled: false,
})

/** 필터가 기본값에서 벗어났는가 — «초기화» 버튼·건수 표시용 */
export function isFiltered(f: RldFilter): boolean {
  return !f.single || !f.combo || !!f.maker.trim() || f.excludedIngredients.length > 0 || f.weightOn
    || !!f.name.trim() || f.referenceOnly || f.cancelled
}

/** 품목의 성분명 — 영문(허가 목록) 우선, 없으면 대조약조회의 한글. `/` 구분, 품목 안 중복 제거 */
export function ingredientsOf(row: Row): string[] {
  const raw = (row.eng_ingredient ?? row.ingredient) as string | null | undefined
  const parts = (raw ?? '').split('/').map((p) => p.trim()).filter(Boolean)
  return [...new Set(parts)]
}

/** 목록에 나온 성분명 — 빈도 내림차순(같으면 이름순) */
export function ingredientChips(rows: Row[]): { name: string; count: number }[] {
  const counts = new Map<string, number>()
  for (const row of rows) for (const name of ingredientsOf(row)) counts.set(name, (counts.get(name) ?? 0) + 1)
  return [...counts].map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
}

/** 총중량 구간. 상세를 조회했는데 값이 없으면 `none`(바이오·액상 등 산출 불가), 조회 전이면 `unknown` */
export function weightBand(row: Row): WeightBand {
  const w = Number(row.weight_mg)
  if (row.weight_mg != null && row.weight_mg !== '' && Number.isFinite(w)) {
    if (w <= 300) return 'le300'
    if (w <= 600) return 'le600'
    if (w <= 1000) return 'le1000'
    return 'gt1000'
  }
  return row.enriched ? 'none' : 'unknown'
}

/** 취소·만료 품목의 사유(`허가취소(유효기간만료)` → `유효기간만료`). 정상·모름이면 null */
export function permitStatusLabel(row: Row): string | null {
  const status = row.permit_status as string | null | undefined
  if (!status || status === '정상') return null
  const m = status.match(/\((.+)\)/)
  return m ? m[1] : status
}

const includes = (value: unknown, needle: string) =>
  !needle.trim() || String(value ?? '').toLowerCase().includes(needle.trim().toLowerCase())

/** 총중량을 뺀 필터 — 총중량 필터를 켤 때 조회할 대상(좁혀진 품목)을 고르는 데도 쓴다 */
export function passesBase(row: Row, f: RldFilter): boolean {
  const n = Number(row.ingr_count)
  if (Number.isFinite(n) && n > 0) {
    if (n === 1 && !f.single) return false
    if (n >= 2 && !f.combo) return false
  }
  if (!includes(row.maker, f.maker)) return false
  if (!includes(row.product_name, f.name)) return false
  if (f.referenceOnly && row.is_reference !== true) return false
  if (!f.cancelled && permitStatusLabel(row)) return false
  if (f.excludedIngredients.length && ingredientsOf(row).some((i) => f.excludedIngredients.includes(i))) return false
  return true
}

/** 필터를 통과한 품목의 원래 위치 번호. 총중량 조회 전 품목(`unknown`)은 조회 중으로 보고 남긴다 */
export function applyFilter(rows: Row[], f: RldFilter): number[] {
  const out: number[] = []
  rows.forEach((row, i) => {
    if (!passesBase(row, f)) return
    if (f.weightOn) {
      const band = weightBand(row)
      if (band !== 'unknown' && !f.weightBands.includes(band)) return
    }
    out.push(i)
  })
  return out
}
